import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Chip, CircularProgress, FormControlLabel, IconButton, Switch, Tooltip } from '@mui/material';
import React from 'react';
import { useAuthProvider, useNotify, useRecordContext } from 'react-admin';
import type { OpenBalenaAuthProvider, OpenBalenaSession } from '../authProvider/openbalenaAuthProvider';
import environment from '../lib/reactAppEnv';

type DeviceRecord = { uuid: string };

const DevicePublicUrl: React.FC = () => {
  const record = useRecordContext<DeviceRecord>();
  const authProvider = useAuthProvider<OpenBalenaAuthProvider>();
  const notify = useNotify();

  const [enabled, setEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [initialised, setInitialised] = React.useState(false);

  const uuid = typeof record?.uuid === 'string' ? record.uuid : '';
  const remoteBase = environment.REACT_APP_OPEN_BALENA_REMOTE_URL ?? '';
  const publicUrl = uuid ? `${remoteBase}/public/${uuid}/` : '';

  React.useEffect(() => {
    if (!uuid || !remoteBase || initialised) return;

    void fetch(`${remoteBase}/public-url/${uuid}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data: { enabled?: boolean }) => {
        setEnabled(!!data.enabled);
      })
      .catch(() => {/* ignore */})
      .finally(() => setInitialised(true));
  }, [uuid, remoteBase, initialised]);

  const handleToggle = React.useCallback(
    async (next: boolean) => {
      if (!uuid) return;
      setLoading(true);
      try {
        const session = authProvider?.getSession?.() as OpenBalenaSession | undefined;
        if (next) {
          const resp = await fetch(`${remoteBase}/public-url`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uuid, jwt: session?.jwt, port: 80 }),
          });
          if (!resp.ok) {
            const err = (await resp.json().catch(() => ({ error: resp.statusText }))) as { error?: string };
            throw new Error(err.error ?? resp.statusText);
          }
          setEnabled(true);
          notify('Public URL enabled', { type: 'success' });
        } else {
          const resp = await fetch(`${remoteBase}/public-url/${uuid}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (!resp.ok) throw new Error(resp.statusText);
          setEnabled(false);
          notify('Public URL disabled', { type: 'info' });
        }
      } catch (e) {
        notify(`Error: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [authProvider, notify, remoteBase, uuid],
  );

  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
      <FormControlLabel
        control={
          loading ? (
            <CircularProgress size={20} sx={{ mx: '9px' }} />
          ) : (
            <Switch
              checked={enabled}
              disabled={!initialised}
              onChange={(e) => void handleToggle(e.target.checked)}
              size='small'
            />
          )
        }
        label={<span style={{ fontSize: '13px' }}>Public URL</span>}
        sx={{ mr: 0 }}
      />

      {enabled && publicUrl && (
        <>
          <Chip
            label={publicUrl}
            size='small'
            sx={{ fontFamily: 'monospace', fontSize: '11px', maxWidth: 340, cursor: 'pointer' }}
            onClick={() => window.open(publicUrl, '_blank')}
          />
          <Tooltip title='Copy URL'>
            <IconButton
              size='small'
              onClick={() => {
                void navigator.clipboard.writeText(publicUrl);
                notify('Copied to clipboard', { type: 'info' });
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title='Open in new tab'>
            <IconButton size='small' onClick={() => window.open(publicUrl, '_blank')}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default DevicePublicUrl;
